import styles from "./AddCategory.module.css";
import { ReactComponent as MarkerSelect1 } from "../../../../img/icon/mk1_select.svg";
import { ReactComponent as MarkerSelect2 } from "../../../../img/icon/mk2_select.svg";
import { ReactComponent as MarkerSelect3 } from "../../../../img/icon/mk3_select.svg";
import { CategoryOptions, markerColors } from "../../../common/Common";
import icons from "../../../../img/icon/icons";
import { ChangeEvent, Dispatch, SetStateAction, useEffect, useState } from "react";
import Icon from "../../../common/Icon";
import { SubContentList } from "./SinglePlaceFormArea";
import { ButtonProps } from "../../MiniButton";
import BottomButtonBox from "../../BottomButtonBox";

interface AddCategoryProps {
    categories: CategoryOptions[];
    setSubContent: Dispatch<SetStateAction<SubContentList | undefined>>;
    addNewCategory: (category: CategoryOptions) => void;
}

/**
 * @author L.chanwoo
 * @date 2024-09-25 00:09:40
 * @remark 카테고리 추가화면
 * @param props AddCategoryProps
 * setCategory :  현재 선택된 카테고리 이름
 * categorys : 카테고리 목록
 * setCategorys : 새로운 카테고리 설정
 *
 */
const AddCategory = ({ categories, ...props }: AddCategoryProps) => {
    const icon = categories.length && categories[0].icon ? categories[0].icon : "markerSelect1";
    const [selectColorIdx, setSelectColorIdx] = useState<number>(0);
    const [categoryName, setCategoryName] = useState<string>("");
    const [errorMsg, setErrorMsg] = useState<string>("");
    const [inputValidationResult, setInputValidationResult] = useState<boolean>(false);

    useEffect(() => {
        inputValidation();
    }, [categoryName]);

    const addCategory = () => {
        const newCategory: CategoryOptions = {
            disabled: false,
            color: markerColors[selectColorIdx],
            label: categoryName,
            icon: icon,
        };
        props.addNewCategory(newCategory);
        props.setSubContent(SubContentList.categoryList);
    };
    const cancel = () => {
        props.setSubContent(SubContentList.categoryList);
    };

    const activeRightButton: ButtonProps = {
        label: "확인",
        onClick: addCategory,
    };
    const deActiveRightButton: ButtonProps = {
        label: "확인",
        type: "gray",
    };

    const leftButton: ButtonProps = {
        label: "취소",
        type: "gray",
        onClick: cancel,
    };

    const clickColor = (idx: number) => {
        setSelectColorIdx(idx);
    };

    const renderPalette = () => {
        return markerColors.map((data, idx) => {
            return (
                <button
                    key={idx}
                    className={`${styles.colorItem} ${idx === selectColorIdx ? styles.selectColor : ""}`}
                    style={{ backgroundColor: data }}
                    onClick={() => clickColor(idx)}
                />
            );
        });
    };

    const renderMarker = () => {
        const markerItems: string[] = ["markerSelect1", "markerSelect2", "markerSelect3"];
        const markerStyle = markerItems.map((markerName) => {
            return {
                WebkitMaskImage: `url('${icons[markerName]}')`,
                maskImage: `url('${icons[markerName]}')`,
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center center",
                WebkitMaskPosition: "center center",
                maskSize: "2.2rem",
                WebkitMaskSize: "2rem",
                backgroundColor: markerColors[selectColorIdx],
            };
        });

        return (
            <>
                <div className={icon == "markerSelect1" ? styles.act : styles.deact}>
                    <MarkerSelect1
                        id="markerSelect1"
                        className={`${styles.markerItem} ${icon == "markerSelect1" && selectColorIdx != -1 ? styles.colored : styles.uncolored}`}
                        style={icon != "markerSelect1" ? { backgroundColor: "grey" } : selectColorIdx == -1 ? {} : markerStyle[0]}
                    />
                </div>
                <div className={icon == "markerSelect2" ? styles.act : styles.deact}>
                    <MarkerSelect2
                        id="markerSelect2"
                        className={`${styles.markerItem} ${icon == "markerSelect2" && selectColorIdx != -1 ? styles.colored : styles.uncolored}`}
                        style={icon != "markerSelect2" ? { backgroundColor: "grey" } : selectColorIdx == -1 ? {} : markerStyle[1]}
                    />
                </div>
                <div className={icon == "markerSelect3" ? styles.act : styles.deact}>
                    <MarkerSelect3
                        id="markerSelect3"
                        className={`${styles.markerItem} ${icon == "markerSelect3" && selectColorIdx != -1 ? styles.colored : styles.uncolored}`}
                        style={icon != "markerSelect3" ? { backgroundColor: "grey" } : selectColorIdx == -1 ? {} : markerStyle[2]}
                    />
                </div>
            </>
        );
    };

    const inputValidation = () => {
        const labels = Object.values(categories).map((category: CategoryOptions) => {
            return category.label;
        });
        if (labels.includes(categoryName)) {
            setInputValidationResult(false);
            setErrorMsg("이미 동일한 이름의 카테고리가 존재합니다");
        } else {
            if (categoryName == "") {
                setInputValidationResult(false);
                setErrorMsg("");
            } else if (categoryName.replaceAll(" ", "") == "") {
                setInputValidationResult(false);
                setErrorMsg("공백 이외의 문자로 입력해주세요");
            } else if (categoryName.trim().length != categoryName.length) {
                setInputValidationResult(false);
                setErrorMsg("글자 앞뒤에 있는 공백을 지우고 입력해주세요");
            } else if (categoryName.length > 200) {
                setInputValidationResult(false);
                setErrorMsg("200자 이내로 입력해주세요");
            } else {
                setInputValidationResult(true);
                setErrorMsg("");
            }
        }
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        setCategoryName(value);
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.top}>
                <div className={styles.category}>
                    <div className={styles.mkstyle}>
                        {<Icon name="check" color={inputValidationResult ? "default" : "lightGrey"} />}
                        <span>{"카테고리 이름"}</span>
                    </div>
                    <input
                        className={categoryName == "" ? styles.default : inputValidationResult ? styles.valid : styles.invalid}
                        placeholder="카테고리 추가"
                        onChange={handleChange}
                    />
                    <div className={errorMsg == "" ? styles.accurate : styles.error}>
                        <span>{errorMsg}</span>
                    </div>
                </div>
                <div className={styles.mkstyle}>{"모양 및 색상"}</div>
                <div className={styles.color}>{renderPalette()}</div>
                <div className={styles.marker}>{renderMarker()}</div>
                {/* <div className={styles.desc}>{"카테고리 추가는 장소 등록 완료 후 저장됩니다"}</div> */}
            </div>
            <div className={styles.bottom}>
                <BottomButtonBox right={inputValidationResult ? activeRightButton : deActiveRightButton} left={leftButton} />
            </div>
        </div>
    );
};

export default AddCategory;
